import pageStyles from "./goalPage.module.scss";
import { useEffect } from "react";
import Sticky from "../../../../common/components/sticky";
import { trackEvent } from "@kunduz/utility-analytics";
import { ILearning } from "@kunduz/data-access-learning-api";
import { NextSeo } from "next-seo";
import { useTranslation } from "next-i18next";
import QuestionSideCtaLoader, {
  SideCTAModes,
} from "../../../../common/components/sideCta";
import { LearningFilterMenu } from "../../components/filterMenu/learningFilterMenu";
import Link from "next/link";
import { LearningBreadcrumb } from "../../components/learningBreadcrumb/learningBreadcrumb";
import { LearningPathOrModuleItem } from "../../components/pathOrModuleItem/learningPathOrModuleItem";
import { LearningPagination } from "../../components/pagination/learningPagination";
import { getContentUrl, ReservedWords } from "@kunduz/utility-common";
import {
  BlogQIMobileCta,
  HeaderComponent,
  WebsiteTrFooter,
} from "@kunduz/feature-landing-pages";
import { type IContent } from "@kunduz/data-access-cms-api";

const GoalPage = (props: GoalPageProps) => {
  const {
    pageMeta: meta,
    isMobile,
    lang,
    filterItems,
    pathItems,
    currentPage,
    perPage,
  } = props;
  const activeSlug = meta.slug.substring(1, meta.slug.length - 1);
  const { t } = useTranslation("kunduzcom_learning");

  useEffect(() => {
    trackEvent("Student Goal Page Viewed", {
      goal: props.pageMeta.name,
    });
  }, []);

  /**
   * callback for aside menu items
   */
  const handleMenuItemClicked = (payload: any) =>
    trackEvent("Student Goal Item Clicked", { ...payload });

  const pageTitleKeysByType = {
    [ReservedWords.TR_CONTENT_EXAM]: "learning_goal_page_title_type_exam",
    [ReservedWords.TR_CONTENT_GRADE]: "learning_goal_page_title_type_grade",
    [ReservedWords.TR_CONTENT_COURSE]: "learning_goal_page_title_type_course",
  };

  return (
    <>
      <NextSeo
        title={props.pageMeta.seo_meta.meta_title}
        description={props.pageMeta.seo_meta.meta_desc}
        canonical={getContentUrl({
          lang: props.lang,
          contentType: props.type,
          domain: process.env.NEXT_PUBLIC_DOMAIN_PREFIX,
          segments: [meta.slug],
        })}
        robotsProps={{
          maxImagePreview: "large",
        }}
      />

      <HeaderComponent
        language={props.lang}
        headerContactShow={false}
        headerBannerShow={!!props.headerBanner}
        headerBannerData={props.headerBanner}
        headerShow={!!props.headerItems}
        headerItems={props.headerItems}
      />

      <BlogQIMobileCta />

      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.breadcrumb}>
          <LearningBreadcrumb
            items={[{ name: meta.name }]}
            homePageLabel={t("learning_breadcrumb_homepage_label")}
            lang={props.lang}
            linkAs={Link}
          />
        </div>
        <main className={pageStyles.content}>
          <LearningFilterMenu
            grades={filterItems.grades}
            exams={filterItems.exams}
            courses={filterItems.courses}
            active={activeSlug}
            isMobile={isMobile}
            onItemClicked={handleMenuItemClicked}
            linkAs={Link}
            lang={props.lang}
          />
          <div className={pageStyles.pathContainer}>
            <div className={pageStyles.headersContainer}>
              <h1>
                {t(pageTitleKeysByType[props.type], { goal_name: meta.name })}
              </h1>
              {meta.seo_meta?.sub_title && (
                <div className={pageStyles.subTitle}>
                  {meta.seo_meta.sub_title}
                </div>
              )}
              {meta.seo_meta?.short_desc && (
                <div className={pageStyles.shortDesc}>
                  {meta.seo_meta.short_desc}
                </div>
              )}
            </div>
            <div className={pageStyles.moduleItemsContainer}>
              {pathItems?.results?.map((item) => (
                <LearningPathOrModuleItem
                  key={item.id}
                  description={item.seo_meta.sub_title}
                  slug={getContentUrl({
                    lang: props.lang,
                    contentType: ReservedWords.TR_CONTENT_PATH_DETAIL,
                    segments: [item.slug],
                  })}
                  img={item.logo}
                  isMobile={props.isMobile}
                  name={item.name}
                  numberOfUnits={item.stats_meta.number_of_units}
                  unitLabel={t("learning_number_of_modules_icon_label")}
                  numberOfVideos={item.stats_meta.number_of_videos}
                  videosLabel={t(
                    "learning_number_of_video_components_icon_label",
                  )}
                  numberOfSubjects={item.stats_meta.number_of_subjects}
                  subjectsLabel={t("learning_number_of_concepts_icon_label")}
                  goPathLabel={t("learning_learning_path_item_view_unit")}
                  lang={props.lang}
                  linkAs={Link}
                />
              ))}
              {pathItems.count > perPage && (
                <LearningPagination
                  currentPage={currentPage}
                  perPage={perPage}
                  total={pathItems.count}
                  previousText={t("learning_pagination_previous_label")}
                  nextText={t("learning_pagination_next_label")}
                  lang={props.lang}
                  linkAs={Link}
                />
              )}
            </div>
            {meta.seo_meta?.long_desc && (
              <div className={pageStyles.longDescription}>
                {meta.seo_meta.long_desc}
              </div>
            )}
          </div>
        </main>
        <div className={pageStyles.sticky}>
          <Sticky top={100}>
            <QuestionSideCtaLoader
              isMobile={isMobile}
              lang={lang}
              mode={SideCTAModes.LEARNING}
              translationFile="kunduzcom_learning"
            />
          </Sticky>
        </div>
      </div>

      <WebsiteTrFooter />
    </>
  );
};

export interface GoalPageProps {
  lang: "tr" | "en";
  isMobile: boolean;
  isAuth: boolean;
  pageMeta: ILearning.PathListResponse;

  filterItems: {
    courses: ILearning.LearningFilterMenuItem[];
    grades: ILearning.LearningFilterMenuItem[];
    exams: ILearning.LearningFilterMenuItem[];
  };
  pathItems: {
    count: number;
    results: ILearning.LearningPathItem[];
  };
  perPage: number;
  currentPage: number;
  type: ReservedWords;
  headerItems: IContent.HeaderItemsResponse;
  headerBanner?: IContent.HeaderBannerResponse;
}

export default GoalPage;
